<template>
  <div v-if="testimonials.length" :class="{ 'content-block': !previewModeOnly }">
    <div class="text-center">
      <div v-if="!previewModeOnly" class="subtitle">Testimonials</div>
      <h2 v-if="!previewModeOnly" class="mb-6">What people say about us</h2>
      <v-carousel
        v-model="currentSlide"
        cycle
        hide-delimiters
        show-arrows="hover"
        class="testimonials-carousel"
        height="auto"
      >
        <v-carousel-item v-for="(response, i) in testimonialComputed" :key="i" class="testimonials-carousel__item">
          <div class="testimonials-carousel__item-text">
            {{ response.testimonial }}
          </div>
        </v-carousel-item>
      </v-carousel>

      <div class="testimonials-pagination">
        <div
          v-for="(response, i) in testimonialComputed"
          :key="i"
          class="testimonials-pagination__item"
          :class="{ active: currentSlide === i }"
          @click.native="currentSlide = i"
        >
          <img :src="response.image" alt="" />
          <p v-if="currentSlide === i">
            {{ response.name }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from "vue-facing-decorator";
import { useDisplay, DisplayInstance } from "vuetify";

const namespace: string = "property";

@Component({})
export default class TestimonialsCarousel extends Vue {
  @Prop({ type: Boolean }) previewModeOnly!: boolean;
  @Prop({ type: String }) organizationId: string;
  @Prop({ type: String }) propertyId: string;

  get testimonials() {
    return this.$store.getters[`${namespace}/testimonials`];
  }

  get testimonialsLoaded() {
    return this.$store.getters[`${namespace}/testimonialsLoaded`];
  }

  breakpoint: DisplayInstance;
  async mounted() {
    this.breakpoint = useDisplay();
    if (this.organizationId || this.propertyId) {
      this.loadTestimonials();
    }
  }

  @Watch("organizationId")
  async onOrganizationIdChange(val) {
    if (val) {
      this.loadTestimonials();
    }
  }

  @Watch("propertyId")
  async onPropertyIdChange(val) {
    if (val) {
      this.loadTestimonials();
    }
  }

  get testimonialComputed() {
    if (this.breakpoint.xs.value) {
      return [...this.testimonials].splice(0, 6);
    }
    if (this.breakpoint.smAndDown.value) {
      return [...this.testimonials].splice(0, 8);
    }

    return this.testimonials;
  }

  async loadTestimonials() {
    try {
      const testimonials = this.propertyId
        ? await this.$api.testimonial.getPropertyTestimonials(this.propertyId)
        : await this.$api.testimonial.getOrganizationTestimonials(this.organizationId);
      this.$store.dispatch(
        `${namespace}/setTestimonials`,
        this.propertyId ? testimonials : testimonials.filter((item) => item.show_on_organization)
      );
    } catch (e) {
      await this.$store.dispatch("ui/showError", "Couldn't load testimonials");
    }
  }

  currentSlide = 0;
}
</script>

<style lang="scss">
@import "@/assets/styles/variables.scss";

.testimonials-carousel {
  min-height: 10rem;
  &__item {
    &-text {
      font-size: 1.125rem;
      text-align: center;
      max-width: 46.25rem;
      padding: 0.75rem;
      margin: 0 auto;
      color: var(--v-textSecondary-base);
    }
  }
}

.subtitle {
  font-size: 0.75rem;
  color: var(--v-primary-base);
  text-transform: uppercase;
  margin-bottom: 0.66rem;
  font-family: $headers-font;
  font-weight: 600;
}

.testimonials-pagination {
  display: flex;
  justify-content: center;
  min-height: 8.125rem;
  &__item {
    cursor: pointer;
    width: 6.375rem;
    text-align: center;
    img {
      height: 2.625rem;
      width: 2.625rem;
      display: block;
      object-fit: cover;
      border-radius: 50%;
      overflow: hidden;
      margin: 11px auto;
      transition: all 0.2s ease;
    }

    p {
      text-align: center;
      font-size: 0.875rem;
      color: #252631;
      font-weight: 500;
      line-height: 1.2;
      min-height: 2rem;
      margin: 0;
    }

    &.active {
      img {
        margin-top: 0;
        height: 4rem;
        width: 4rem;
      }
    }
  }
}
</style>
